import * as React from 'react';
import Table from 'react-bootstrap/Table';
import { Card } from 'react-bootstrap';
import { postRequest } from '../../lib/api';
import { AuthContext } from '../../authContext/context';
import { Spinner } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { checkPermission } from '../../utils/checkPermissions';

export default function Dashboard() {
  const authCtx = React.useContext(AuthContext);
  const [groupCodes, setGroupCodes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  
  // Fetch group codes breakdown with dynamic counts and amounts
  React.useEffect(() => {
    const fetchGroupCodes = async () => {
      try {
        const response = await postRequest('private/getGroupCodeBreakdown', {}, authCtx.token);

        console.log('API Response:', response);

        if (response?.data) {
          console.log('Group codes breakdown:', response.data);
          setGroupCodes(response.data);
        }
      } catch (error) {
        console.error('Error fetching group codes breakdown:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGroupCodes();
  }, [authCtx.token]);
  
  // Check permissions after all hooks
  const userPermissions = authCtx.permissions;
  const hasCustomerPermission = userPermissions?.customers && (
    userPermissions.customers.create || 
    userPermissions.customers.view_details || 
    userPermissions.customers.edit || 
    userPermissions.customers.delete
  );
  
  // Redirect to customers if user only has customer permissions
  if (userPermissions && hasCustomerPermission && !userPermissions?.leads?.view) {
    return <Navigate to="/customers" replace />;
  }

  return (
    <>
      <div className='page-header'>
        <h1 className='mt-0'>Dashboard</h1>
      </div>
      
      <Card className="mb-4">
        <Card.Body>
          <h4 className="mb-4">Group Codes Breakdown</h4>
          <div className="table-responsive">
            <Table bordered hover style={{ fontSize: '14px' }}>
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ fontWeight: '600', minWidth: '100px' }}>Code</th>
                  <th style={{ fontWeight: '600', minWidth: '120px' }}>New</th>
                  <th style={{ fontWeight: '600', minWidth: '150px' }}>Quote Sent</th>
                  <th style={{ fontWeight: '600', minWidth: '150px' }}>Instructed</th>
                  <th style={{ fontWeight: '600', minWidth: '120px' }}>Exported</th>
                  <th style={{ fontWeight: '600', minWidth: '150px' }}>Not Interested</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center">
                      <Spinner animation="border" size="sm" />
                    </td>
                  </tr>
                ) : groupCodes.length > 0 ? (
                  <>
                    {groupCodes.map((row, index) => (
                      <tr key={index}>
                        <td style={{ fontWeight: '500' }}>{row.code}</td>
                        <td>
                          {row.new.count} / £{row.new.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td>
                          {row.quoteSent.count} / £{row.quoteSent.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td>
                          {row.instructed.count} / £{row.instructed.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td>
                          {row.exported.count} / £{row.exported.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td>
                          {row.notInterested.count} / £{row.notInterested.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                    {/* Total Row */}
                    <tr style={{ backgroundColor: '#e3f2fd', fontWeight: '700' }}>
                      <td>TOTAL</td>
                      <td>
                        {groupCodes.reduce((sum, row) => sum + (row.new?.count || 0), 0)} / £{groupCodes.reduce((sum, row) => sum + (row.new?.amount || 0), 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td>
                        {groupCodes.reduce((sum, row) => sum + (row.quoteSent?.count || 0), 0)} / £{groupCodes.reduce((sum, row) => sum + (row.quoteSent?.amount || 0), 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td>
                        {groupCodes.reduce((sum, row) => sum + (row.instructed?.count || 0), 0)} / £{groupCodes.reduce((sum, row) => sum + (row.instructed?.amount || 0), 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td>
                        {groupCodes.reduce((sum, row) => sum + (row.exported?.count || 0), 0)} / £{groupCodes.reduce((sum, row) => sum + (row.exported?.amount || 0), 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td>
                        {groupCodes.reduce((sum, row) => sum + (row.notInterested?.count || 0), 0)} / £{groupCodes.reduce((sum, row) => sum + (row.notInterested?.amount || 0), 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center">No group codes found</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <h4 className="mb-4">Group Code Counts</h4>
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {groupCodes.map((groupCode, index) => {
                // Generate different colors for each code
                const colors = [
                  '#dc3545', '#ff6b6b', '#fd7e14', '#ffc107', '#ffeb3b', '#cddc39',
                  '#8bc34a', '#4caf50', '#009688', '#00bcd4', '#03a9f4', '#2196f3',
                  '#3f51b5', '#673ab7', '#9c27b0', '#e91e63', '#607d8b', '#795548',
                  '#ff5722', '#00acc1', '#0097a7'
                ];
                const bgColor = colors[index % colors.length];
                const textColor = ['#ffc107', '#ffeb3b', '#cddc39'].includes(bgColor) ? 'dark' : 'white';

                // Calculate total count across all statuses
                const totalCount =
                  (groupCode.new?.count || 0) +
                  (groupCode.quoteSent?.count || 0) +
                  (groupCode.instructed?.count || 0) +
                  (groupCode.exported?.count || 0) +
                  (groupCode.notInterested?.count || 0);

                return (
                  <span
                    key={groupCode.code}
                    style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      backgroundColor: bgColor,
                      color: textColor,
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  >
                    {groupCode.code} ({totalCount})
                  </span>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
